// bills.js — canonical due-date math and paid-state rules for public.bills.
//
// THIS FILE IS THE ONE DEFINITION. Three callers share it:
//   - src/Scorify.jsx        (Finance tab rendering, markBillPaid)
//   - api/notify-cron.js     (bill_due trigger + push prompt)  via api/lib/bills.js
//   - api/coach.js           (coach prompt)                    via api/lib/bills.js
//
// It lives under src/ rather than api/lib/ because Create React App's
// ModuleScopePlugin refuses any import from outside src/, so the client could
// not reach api/lib/. api/lib/bills.js is a thin re-export of this file, which
// keeps the server import paths unchanged and keeps the math in one place.
// Browser-safe: no node builtins, no imports.
//
// ── due_day ───────────────────────────────────────────────────────────────────
// bills.due_day is a day-of-month integer (1..31), NOT a date. There is no year
// or month on the row, so "when is this next due" is calendar arithmetic against
// the user's LOCAL today, never a timestamp subtraction.
//
// ── paid_for_month ────────────────────────────────────────────────────────────
// bills.status ('upcoming' | 'paid' | 'overdue') is NOT the paid signal. It was
// written once by markBillPaid and never reset, so a bill marked paid in April
// stayed paid forever and was excluded from every trigger and total after that.
// paid_for_month holds the billing cycle the row was last paid for, as 'YYYY-MM'
// in the user's local time. A bill counts as paid only when it equals the
// current local month, so the flag expires by itself at every month rollover.

/** Last day of a 1-based (year, month). Day 0 of month M+1 is the last day of M. */
function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

/**
 * The IANA zone to reckon "today" in.
 * On the server pass the user's profiles.timezone. In the browser pass nothing
 * and the runtime's own zone is used — which is the whole point, since the
 * person looking at the screen is in it.
 */
export function resolveZone(zone) {
  if (typeof zone === 'string' && zone.trim()) return zone.trim();
  // No zone given: fall back to the RUNTIME's zone. This is correct in a browser,
  // where the runtime zone is the user's own. It is NOT correct on a server — a
  // Vercel container reports 'UTC', so a null profiles.timezone would silently
  // produce UTC answers for a user who lives anywhere else. Server callers must
  // use requireZone() instead and handle null themselves.
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * Strict zone resolution for SERVER use: returns the zone, or null when unknown.
 * Never guesses. Callers decide what an unknown zone means rather than being
 * handed a wrong answer that looks right.
 */
export function requireZone(zone) {
  return (typeof zone === 'string' && zone.trim()) ? zone.trim() : null;
}

/**
 * The user's local calendar date, as integers.
 *
 * Uses the 'en-CA' / YYYY-MM-DD idiom, then splits the string into integers
 * rather than re-parsing it into a Date. Re-parsing would reintroduce the UTC
 * drift this exists to avoid: at 21:00 on the 30th in America/Los_Angeles it is
 * already the 1st in UTC, which shifts every bill by a day and, at a month
 * boundary, by a whole month.
 */
export function localToday(zone, atMs) {
  // `atMs` (optional epoch ms) pins the instant — the server replay tool uses
  // it to rebuild a past day's picture. Omitted everywhere else: now.
  const s = new Intl.DateTimeFormat('en-CA', { timeZone: resolveZone(zone) }).format(atMs != null ? new Date(atMs) : new Date());
  const [y, m, d] = s.split('-').map(Number);
  return { y, m, d };
}

/** Current local billing cycle as 'YYYY-MM' — the value markBillPaid writes. */
export function currentMonth(zone, atMs) {
  const { y, m } = localToday(zone, atMs);
  return `${y}-${String(m).padStart(2, '0')}`;
}

/**
 * Whole days from local today until the next occurrence of `dueDay`.
 *
 *   dueDay >= today  -> due later this month:  dueDay - today
 *   dueDay <  today  -> already passed this month, so it rolls into next month:
 *                       (days left in this month) + dueDay
 *
 * Returns 0 when due today, and null when dueDay is missing or out of range
 * (callers drop those rather than guess).
 *
 * Month rollover is the case a naive `dueDay - today` gets wrong: it returns a
 * negative for a bill that has already passed this month, when the bill is in
 * fact due soon in the NEXT month. Worked example, 31-day month, today = 29,
 * dueDay = 1:  (31 - 29) + 1 = 3 days. Not -28.
 *
 * Both months are clamped with Math.min(dueDay, daysInMonth) so a due_day that
 * overshoots a short month lands on that month's last day instead of rolling
 * past it: due_day 31 is the 28th in February, and due_day 29 (which exists in
 * this database) is the 28th in a non-leap February. The superseded client
 * version hardcoded 31 here, which overstated by 3 days every February.
 */
export function daysUntilDue(dueDay, todayY, todayM, todayD) {
  const day = Number(dueDay);
  if (!Number.isInteger(day) || day < 1 || day > 31) return null;

  const dimThis = daysInMonth(todayY, todayM);
  const dueThis = Math.min(day, dimThis);
  if (dueThis >= todayD) return dueThis - todayD;

  // Rolls into next month — carry the year at December.
  const nextY = todayM === 12 ? todayY + 1 : todayY;
  const nextM = todayM === 12 ? 1 : todayM + 1;
  const dueNext = Math.min(day, daysInMonth(nextY, nextM));
  return (dimThis - todayD) + dueNext;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Days until a bill's next charge, cadence-aware (2026-10-20): a weekly bill
 * (cadence 'weekly', weekday 0=Sunday..6) is due on its next weekday (0 =
 * today); everything else is monthly by due_day (daysUntilDue).
 */
export function billDaysUntil(bill, todayY, todayM, todayD) {
  if (bill?.cadence === 'weekly') {
    const wd = Number(bill.weekday);
    if (!Number.isInteger(wd) || wd < 0 || wd > 6) return null;
    const dow = new Date(Date.UTC(todayY, todayM - 1, todayD, 12)).getUTCDay();
    return (wd - dow + 7) % 7;
  }
  if (bill?.cadence === 'yearly') return yearlyDaysUntil(bill, todayY, todayM, todayD);
  return daysUntilDue(bill?.due_day, todayY, todayM, todayD);
}

/**
 * A yearly bill's due date in `year` ('YYYY-MM-DD', day clamped to the month's
 * length), or null without a usable due_month/due_day.
 */
export function yearlyDueDate(bill, year) {
  const mo = Number(bill?.due_month), day = Number(bill?.due_day);
  if (!Number.isInteger(mo) || mo < 1 || mo > 12 || !Number.isInteger(day) || day < 1 || day > 31) return null;
  return `${year}-${String(mo).padStart(2, '0')}-${String(Math.min(day, daysInMonth(year, mo))).padStart(2, '0')}`;
}
/** Days until a yearly bill's next due date (this year's if not passed, else next year's). */
export function yearlyDaysUntil(bill, todayY, todayM, todayD) {
  const today = Date.UTC(todayY, todayM - 1, todayD);
  for (const y of [todayY, todayY + 1]) {
    const d = yearlyDueDate(bill, y);
    if (!d) return null;
    const ms = Date.parse(`${d}T00:00:00Z`);
    if (ms >= today) return Math.round((ms - today) / 86400000);
  }
  return null;
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The ABSOLUTE local due date, `daysUntil` days after local today, as a timezone-safe
 * object { date:'YYYY-MM-DD', weekday:'Saturday', label:'Saturday, Oct 3' }.
 *
 * Built at NOON UTC from the local calendar numbers (y, m, d + daysUntil) so
 * toISOString()/getUTCDay() read the intended calendar day with no DST/midnight drift.
 * Giving downstream an absolute day (not just a relative count) is what stops a model
 * from recomputing and drifting "due today/tomorrow" by a day — the live SCORIFY bug
 * where the check-in paraphrased "due in 2 days" as "tomorrow".
 */
export function resolvedDueDate(todayY, todayM, todayD, daysUntil) {
  if (daysUntil == null) return null;
  const dt = new Date(Date.UTC(todayY, todayM - 1, todayD + daysUntil, 12, 0, 0));
  if (isNaN(dt.getTime())) return null;
  const date = dt.toISOString().slice(0, 10);
  const weekday = WEEKDAYS[dt.getUTCDay()];
  return { date, weekday, label: `${weekday}, ${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}` };
}

/**
 * Is this bill settled for the CURRENT local cycle?
 * Deliberately ignores `status` — see the paid_for_month note at the top.
 */
export function isPaidThisMonth(bill, monthStr) {
  return typeof bill?.paid_for_month === 'string' && bill.paid_for_month === monthStr;
}

/**
 * Attach `daysUntil` and `paidThisMonth` to each bill and sort soonest-first.
 * Rows with an unusable due_day are dropped.
 */
export function annotateBills(bills, zone, atMs) {
  if (!Array.isArray(bills)) return [];
  // Server-only entry point. Without a zone, "due in N days" is unanswerable —
  // returning [] drops the bills section rather than dating every bill in UTC.
  const z = requireZone(zone);
  if (!z) {
    console.warn('annotateBills: no timezone — bills omitted rather than dated in UTC');
    return [];
  }
  const { y, m, d } = localToday(z, atMs);
  const month = `${y}-${String(m).padStart(2, '0')}`;
  return bills
    .map((b) => {
      const daysUntil = billDaysUntil(b, y, m, d);
      const due = resolvedDueDate(y, m, d, daysUntil);
      return {
        ...b,
        daysUntil,
        // A yearly bill is "paid this cycle" when stamped for this year's due month.
        paidThisMonth: b.cadence === 'yearly' ? (b.paid_for_month === (yearlyDueDate(b, y) || '').slice(0, 7)) : isPaidThisMonth(b, month),
        dueDate: due?.date || null,        // absolute local 'YYYY-MM-DD'
        dueWeekday: due?.weekday || null,  // 'Saturday'
      };
    })
    .filter((b) => b.daysUntil !== null)
    .sort((a, b) => a.daysUntil - b.daysUntil);
}

const money = (n) => {
  // Cents first (2026-10-14): a float sum like 49.980000000000004 must never
  // reach a prompt or a screen as more than two decimals.
  const v = Math.round((Number(n) || 0) * 100) / 100;
  // Whole dollars stay clean ($60, not $60.00); anything with cents shows both
  // digits ($410.50, not $410.5).
  const frac = v % 1 === 0 ? 0 : 2;
  return v.toLocaleString('en-US', { minimumFractionDigits: frac, maximumFractionDigits: frac });
};

// The relative count alone ("due in 2 days") let the check-in model drift it to the
// wrong relative word. State the ABSOLUTE day too and tell the model to use it verbatim:
// "due Saturday, Oct 3 (in 2 days)".
const duePhrase = (b) => {
  const n = b.daysUntil;
  const rel = n === 0 ? 'today' : n === 1 ? 'tomorrow' : `in ${n} days`;
  return b.dueWeekday && b.dueDate ? `due ${b.dueWeekday}, ${monthDay(b.dueDate)} (${rel})` : `due ${rel}`;
};
const monthDay = (iso) => {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${MONTHS[Number(m[1]) - 1]} ${Number(m[2])}` : '';
};

/**
 * The UPCOMING BILLS prompt block. Returns '' when there is nothing to show, so
 * the caller can drop the section rather than print an empty header.
 *
 * The 7-day total counts bills not yet paid for this cycle — a bill already
 * settled is not money still leaving the account, and counting it would
 * overstate what is actually coming.
 */
export function formatBillsBlock(annotated) {
  if (!Array.isArray(annotated) || annotated.length === 0) return '';

  // A bill already paid for this cycle is NOT upcoming. It used to sit in the
  // same list as "due Friday, Oct 16 (tomorrow)" with a trailing "already paid
  // this month" tag, and the model led the 2026-10-15 check-in with "Car
  // bill's due tomorrow" while its own body admitted the bill was paid. The
  // paid test is the one place (isPaidThisMonth, via annotateBills); here it
  // decides which list the bill goes in, and the paid list carries no due
  // phrase at all.
  const unpaid = annotated.filter((b) => !b.paidThisMonth);
  const paid = annotated.filter((b) => b.paidThisMonth);

  const lines = unpaid.map((b) => {
    const tags = [b.category || 'uncategorized'];
    if (b.autopay === true) tags.push('autopay');
    return `  ${b.name}: $${money(b.amount)} ${duePhrase(b)} (${tags.join(', ')})`;
  });

  const next7 = unpaid.filter((b) => b.daysUntil <= 7);
  const total7 = next7.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

  const out = [];
  if (unpaid.length) {
    out.push(
      'UPCOMING BILLS (state each bill\'s due day EXACTLY as written — the day/date is already computed; never recompute "today"/"tomorrow" yourself):',
      ...lines,
      `  Total due in the next 7 days: $${money(total7)} across ${next7.length} unpaid bill${next7.length === 1 ? '' : 's'}.`,
    );
  } else {
    out.push('UPCOMING BILLS: none unpaid this cycle.');
  }
  if (paid.length) {
    out.push(
      'ALREADY PAID THIS CYCLE (settled; NOT due, NOT upcoming, NOT a to-do — never say one of these is due, and never lead with it):',
      ...paid.map((b) => `  ${b.name}: $${money(b.amount)} (paid for this cycle${b.autopay === true ? ', autopay' : ''})`),
    );
  }
  return out.join('\n');
}

/**
 * Bills worth waking Kai for: due within `withinDays`, not on autopay, not
 * already paid for this cycle.
 *
 * autopay is checked as `!== true` rather than `=== false` so a null autopay
 * (unknown) is treated as manual. Assuming a bill pays itself is the costlier
 * mistake — a missed payment is worse than one extra nudge.
 */
export function billsNeedingAction(annotated, withinDays = 3) {
  if (!Array.isArray(annotated)) return [];
  return annotated.filter(
    (b) => b.daysUntil <= withinDays && b.autopay !== true && !b.paidThisMonth
  );
}

/**
 * Which of the two notification windows a bill is in.
 *
 * The windows are separate so a bill can be announced once as a heads-up and
 * once on the day itself. They are keyed per bill (below) rather than per
 * trigger type, because type-level suppression muted the reminder for every
 * bill the moment any one bill had been mentioned.
 */
export function billPhase(daysUntil) {
  // Three distinct one-time notices, no daily repeats between (2026-09-30):
  //   heads_up (2-3 days out) -> due_tomorrow (1 day) -> due_today (0).
  // Each is its own per-bill key, so the 72h suppression fires each exactly
  // once. A bill added late (already 1 day out) still gets due_tomorrow then
  // due_today; one added at day 3 or 2 gets the single heads-up first.
  if (daysUntil === 0) return 'due_today';
  if (daysUntil === 1) return 'due_tomorrow';
  if (daysUntil >= 2 && daysUntil <= 3) return 'heads_up';
  return null;
}

/**
 * The notification_log.trigger_type written for a bill reminder.
 *
 * Shape: `bill_due:<bill id>:<phase>`. The existing suppression logic keys on
 * the whole trigger_type string, so encoding bill and phase into it gives
 * per-bill, per-window suppression with no change to how any other trigger type
 * is suppressed. baseTriggerType() strips it back to 'bill_due' for display.
 */
export function billTriggerKey(bill) {
  const phase = billPhase(bill.daysUntil);
  if (!phase) return null;
  return `bill_due:${bill.id}:${phase}`;
}

/** 'bill_due:abc-123:heads_up' -> 'bill_due'. Any other type passes through. */
export function baseTriggerType(triggerType) {
  return String(triggerType || '').split(':')[0];
}

/**
 * The TOPIC of a trigger key — the identity two near-in-time sends share for
 * the deterministic anti-repetition rules (adjacency + ceiling in
 * api/notify-cron.js). Strips ONLY a trailing bill phase, so
 * bill_due:<id>:heads_up and bill_due:<id>:due_today are one topic (the same
 * bill), while every other key — goal_at_risk:<id>, spend_pace, sleep_debt —
 * is its own topic. Deliberately NOT baseTriggerType (which collapses all
 * bills to 'bill_due') and NOT the full key (which splits a bill by phase).
 */
export function topicKey(triggerType) {
  const s = String(triggerType || '');
  const m = s.match(/^(bill_due:[^:]+):(?:heads_up|due_tomorrow|due_today)$/);
  return m ? m[1] : s;
}

/** The bill phase segment of a key, or null. 'bill_due:x:heads_up' -> 'heads_up'. */
export function keyPhase(triggerType) {
  const m = String(triggerType || '').match(/^bill_due:[^:]+:(heads_up|due_tomorrow|due_today)$/);
  return m ? m[1] : null;
}

/**
 * The autopay fields a bill Edit save writes (2026-10-21). Using the toggle
 * (`autopayTouched`) records the choice as the user's own
 * (autopay_set_by_user), which backfills and detection then never override;
 * an untouched save never changes that flag.
 */
export function autopayEditFields(bill) {
  return { autopay: bill?.autopay === true, ...(bill?.autopayTouched ? { autopay_set_by_user: true } : {}) };
}

/**
 * The bills a daily check-in may mention (2026-10-21): unpaid this cycle and
 * not on autopay (null autopay = unknown, kept). Paid bills are never in the
 * check-in (2026-10-15); autopay bills pay themselves.
 */
export function checkinBills(annotated) {
  return (Array.isArray(annotated) ? annotated : []).filter(b => b && !b.paidThisMonth && b.autopay !== true);
}

/**
 * The autopay bill's status line (2026-10-22): "Autopay · charges the 24th",
 * "Autopay · charges every Friday", "Autopay · charges Nov 3" (yearly) — or
 * "Paid" once the charge was matched for this cycle. Autopay bills get no
 * Pay button; "Mark paid" lives in Edit for a charge on an unlinked account.
 */
const ORD = (n) => { const v = Number(n) || 1, t = v % 100; return `${v}${t >= 11 && t <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[v % 10] || 'th')}`; };
export function autopayStatusLine(bill, { paid = false, year = null } = {}) {
  if (paid) return 'Paid';
  if (bill?.cadence === 'weekly') return `Autopay · charges every ${WEEKDAYS[Number(bill.weekday)] || 'week'}`;
  if (bill?.cadence === 'yearly') {
    const y = year || new Date().getFullYear();
    const d = yearlyDueDate(bill, y);
    return d ? `Autopay · charges ${MONTHS[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}` : 'Autopay';
  }
  return `Autopay · charges the ${ORD(bill?.due_day)}`;
}
